import type { JourneyTrack } from "./creditMatch";
import { CONTACTS, type RoutingContact } from "./leadRouting";

/**
 * Stage 2: The Unbiased Expert Consultation.
 * Runs after the /free-credit-score user gets their $5 reward + journey track placement.
 * Homie opens in "consult mode" with a track-tailored agenda; the user can optionally
 * escalate to a human (Calendly via /your-match) for deeper help.
 */

export interface Stage2Agenda {
  /** Persona Homie wears for this track */
  expertFraming: string;
  /** Named human expert this track is paired with for escalation */
  humanExpertKey: "randy_nemeroff" | "gordon_oliver";
  /** One-sentence pitch shown on the dashboard card and success screen */
  pitch: string;
  /** Opening message Homie sends in chat */
  greeting: string;
  /** Suggested first-turn quick replies */
  quickReplies: string[];
  /** Bullet-style agenda Homie should push the conversation through */
  agendaBullets: string[];
}

export const STAGE2_AGENDAS: Record<JourneyTrack, Stage2Agenda> = {
  homebuyer_preapproval: {
    expertFraming: "loan strategist",
    humanExpertKey: "randy_nemeroff",
    pitch: "Get a 15-minute loan strategy from your unbiased advisor — rate windows, lender shopping, and your doc checklist.",
    greeting:
      "Hi — I'm your unbiased loan strategist for the next 15 minutes. Since you're pre-approval ready, here's what I'd focus on first: locking the right rate window, shopping at least 3 lenders, and prepping your doc checklist. Where do you want to start?",
    quickReplies: [
      "How do I shop lenders?",
      "When should I lock my rate?",
      "What docs do I need?",
    ],
    agendaBullets: [
      "Push them to shop at least 3 lenders before locking a rate.",
      "Walk through the standard pre-approval doc checklist (W-2s, paystubs, bank statements, ID).",
      "Explain rate-lock windows and when to commit.",
      "Offer to book a free 15-min call with Randy Nemeroff (Hero Benefits Alliance) for side-by-side lender quotes.",
    ],
  },
  homebuyer: {
    expertFraming: "buying coach",
    humanExpertKey: "randy_nemeroff",
    pitch: "Get a 15-minute buying-coach session — affordability check, pre-approval next steps, and an agent intro.",
    greeting:
      "Hi — I'm your unbiased buying coach for the next 15 minutes. Based on your score and timeline, the biggest unlocks for you are: an affordability sanity-check, getting pre-approved, and lining up a buyer's agent. Which one first?",
    quickReplies: [
      "Am I really ready to buy?",
      "How do I get pre-approved?",
      "How do I pick an agent?",
    ],
    agendaBullets: [
      "Run an affordability sanity-check using their budget and savings.",
      "Explain the pre-approval process and why it goes before house-hunting.",
      "Coach them on how to interview a buyer's agent (no dual-agency, ask about negotiation track record).",
      "Offer to book a free 15-min call with Randy Nemeroff (Hero Benefits Alliance) to get pre-approved and lined up with a vetted agent.",
    ],
  },
  renter_buyer_prep: {
    expertFraming: "credit + savings coach",
    humanExpertKey: "gordon_oliver",
    pitch: "Get a 15-minute credit + savings game-plan from your unbiased advisor — score lifts, DPA programs, and a 12-month plan.",
    greeting:
      "Hi — I'm your unbiased credit + savings coach for the next 15 minutes. You're closer than you think. The fastest score lifts come from utilization fixes and dispute cleanup, and there are down-payment-assistance programs that can shave years off your timeline. Want to start with credit or savings?",
    quickReplies: [
      "How can I lift my score fast?",
      "Are there grants I qualify for?",
      "Build my 12-month savings plan",
    ],
    agendaBullets: [
      "Identify the top 2 score-lift moves for them (utilization, disputes, on-time streak).",
      "Point them to Stack and DPA programs in their state.",
      "Sketch a 12-month savings plan toward their target down payment.",
      "Offer to book a free 15-min call with Gordon Oliver at Cambridge Credit Counseling (a 501(c)(3) nonprofit) for a confidential credit review.",
    ],
  },
  renter: {
    expertFraming: "renter advocate",
    humanExpertKey: "gordon_oliver",
    pitch: "Get a 15-minute unbiased read — when buying actually makes sense for you, and the renting moves to make in the meantime.",
    greeting:
      "Hi — I'm your unbiased renter advocate for the next 15 minutes. Renting is the right call for many people right now. Want me to run the rent-vs-buy break-even for your situation, share lease-renewal leverage tips, or set a revisit date?",
    quickReplies: [
      "When does buying beat renting?",
      "How do I negotiate my lease?",
      "When should I revisit this?",
    ],
    agendaBullets: [
      "Run a rent-vs-buy break-even using their rent and target home price.",
      "Share 2-3 lease-renewal negotiation tactics.",
      "Suggest a credit-building plan to unlock buying when they want to revisit.",
      "If they want a human, offer Gordon Oliver at Cambridge Credit Counseling (nonprofit) to build a credit + debt plan. Otherwise, set a revisit in 6-12 months.",
    ],
  },
};

export function getStage2ForTrack(track: string | null | undefined): Stage2Agenda | null {
  if (!track) return null;
  return (STAGE2_AGENDAS as Record<string, Stage2Agenda>)[track] ?? null;
}

/** Resolve the named human expert paired with a Stage 2 track. */
export function getStage2HumanForTrack(
  track: string | null | undefined,
): RoutingContact | null {
  const agenda = getStage2ForTrack(track);
  if (!agenda) return null;
  return CONTACTS[agenda.humanExpertKey] ?? null;
}

/** Session-only dismissal so the user isn't nagged twice in the same visit. */
export const STAGE2_DISMISS_KEY = "ha:stage2-consult:dismissed";

/** Build the consult-mode deep link into Homie for a given track. */
export function stage2HomieHref(track: string, entry: string = "credit-stage2"): string {
  const params = new URLSearchParams({ mode: "consult", track, entry });
  return `/homie?${params.toString()}`;
}

/** Build the system-prompt block injected by the homie-chat edge function. */
export function buildStage2SystemBlock(agenda: Stage2Agenda): string {
  const human = CONTACTS[agenda.humanExpertKey];
  return [
    "",
    "",
    "=== STAGE 2 CONSULT MODE ===",
    `You are operating as the user's unbiased ${agenda.expertFraming}.`,
    "This is a focused 15-minute consultation — keep it tight, actionable, and warm.",
    "Agenda you should naturally guide the user through:",
    ...agenda.agendaBullets.map((b) => `- ${b}`),
    "",
    `Paired human expert for this user: ${human.name} (${human.title}, ${human.organization}).`,
    `When you offer escalation to a real person, name ${human.name} explicitly and mention their org.`,
    "Tone: like a trusted friend who's an expert. No upsell, no fluff.",
    "When (and only when) the user wants to go deeper or needs human help,",
    `suggest booking a free 15-min call with ${human.name} by pointing to the **Book a real person** button below the chat.`,
    "=== END STAGE 2 CONSULT MODE ===",
    "",
  ].join("\n");
}